<template>
  <div>
    <v-row v-if="isMultipleEnvelope">
      <v-col>
        <Alert class="mb-4" color="light-blue">
          <div>
            <i18n-t
              keypath="project.signing-checklist.add.step2.Section4.multiEnvelopeSignerHint.intro"
              tag="span"
              scope="global"
            >
              <template #commonSigner>
                <span class="font-weight-bold">{{ $t('project.signing-checklist.add.step2.Section4.multiEnvelopeSignerHint.commonSigner') }}</span>
              </template>
              <template #uniqueSigner>
                <span class="font-weight-bold">{{ $t('project.signing-checklist.add.step2.Section4.multiEnvelopeSignerHint.uniqueSigner') }}</span>
              </template>
            </i18n-t>
            <ul>
              <i18n-t
                keypath="project.signing-checklist.add.step2.Section4.multiEnvelopeSignerHint.commonSigners"
                tag="li"
                scope="global"
              >
                <template #copy>
                  <span class="font-weight-bold">{{ $t('project.signing-checklist.add.step2.Section4.multiEnvelopeSignerHint.copy', 2) }}</span>
                </template>
              </i18n-t>
              <i18n-t
                keypath="project.signing-checklist.add.step2.Section4.multiEnvelopeSignerHint.uniqueSigners"
                tag="li"
                scope="global"
              >
                <template #copy>
                  <span class="font-weight-bold">{{ $t('project.signing-checklist.add.step2.Section4.multiEnvelopeSignerHint.copy', 1) }}</span>
                </template>
              </i18n-t>
            </ul>
          </div>
        </Alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col xl="10" cols="12" :offset="$vuetify.display.xlAndUp ? 1 : 0">
        <SigningChecklistTemplateSignersDetails
          v-if="addFromTemplate"
          class="mb-6"
          :generic-signers="templateGenericSigners"
          :is-multi-envelope="isMultipleEnvelope"
        />

        <div class="mb-8 d-flex justify-space-between align-center">
          <div>
            <SearchTextField v-model="searchQuery" />
            <p class="mt-2 text--x-small">
              {{ $t('project.signing-checklist.add.step2.Section4.searchMemberHelpText') }}
            </p>
          </div>

          <div class="d-flex align-center">
            <AppTooltip v-if="isWitnessButtonDisplayed" :disabled="!hasWitnessButtonTooltip">
              <template #activator="{ props }">
                <div v-bind="props">
                  <app-button
                    class="witness-button mr-4"
                    type="tonal"
                    variant="neutral"
                    :disabled="isWitnessButtonDisabled"
                    data-locator="witness-button"
                    @click="isWitnessDialogOpen = true"
                  >
                    <template #left-icon>
                      <font-awesome-icon :icon="['fas', 'link']" />
                    </template>
                    {{ $t('project.signing-checklist.add.step2.Section4.addWitness') }}
                  </app-button>
                </div>
              </template>
              {{ witnessButtonTooltip }}
            </AppTooltip>
            <AppTooltip v-if="isSignersOrderButtonDisplayed" :disabled="hasMultipleSigners && !isEditingLimited">
              <template #activator="{ props }">
                <div v-bind="props">
                  <app-button class="role-order-button" type="tonal" variant="neutral"
                    :disabled="!hasMultipleSigners || isEditingLimited" @click="onSignersOrderClick"
                    data-locator="order-signatories-button">
                    <template #left-icon>
                      <font-awesome-icon :icon="['fas', 'list']" />
                    </template>
                    {{ $t('project.signing-checklist.add.step2.Section4.signatoriesOrder') }}
                  </app-button>
                </div>
              </template>
              {{ $t('project.signing-checklist.add.step2.Section4.signatoriesOrderTooltipDisabled') }}
            </AppTooltip>
          </div>
        </div>

        <app-alert-in-page
          v-if="isQualifiedSignatoriesOrderAlertDisplayed"
          variant="warning"
          :dismissible="false"
          :class="{'mb-4': !canAddRecipient}"
        >
          <i18n-t keypath="project.signing-checklist.add.step2.Section4.defineSignatoriesOrder.base" tag="p"
            scope="global">
            <template #defineOrder>
              <span class="font-weight-bold">
                {{ $t("project.signing-checklist.add.step2.Section4.defineSignatoriesOrder.defineOrder") }}
              </span>
            </template>
          </i18n-t>

          <a class="app-link learnMoreLink font-weight-medium" :href="qualifiedSignatureHelpLink" target="_blank">
            {{ $t("common.learnMore") }}
            <font-awesome-icon :icon="['far', 'external-link']" class="ml-1" size="xs" />
          </a>
        </app-alert-in-page>

        <app-alert-in-page
          v-if="!canAddRecipient"
          variant="warning"
          :dismissible="false"
        >
          {{ $t('project.signing-checklist.add.step2.Section4.signersLimitReachedWarning', { recipientsMaxCount: ENVELOPE_RECIPIENTS_MAX_COUNT }) }}
        </app-alert-in-page>

        <EnvelopeUsersSelector :step="file.objectToPost" :is-for-multi-envelope="isMultipleEnvelope"
          :search-query="searchQuery" />
      </v-col>
    </v-row>

    <SignersOrderDialog v-if="signatoriesOrderDialog" :signers="file.objectToPost.signers" :file="file"
      @close="signatoriesOrderDialog = false" @patch-signers-order="validateSignersOrder"
      @reset-signers-order="$emit('resetSigners')" />

    <SignersOrderQualifiedDialog v-if="isSignersOrderQualifiedDialogOpen" :signers="file.objectToPost.signers"
      :file="file" @close="isSignersOrderQualifiedDialogOpen = false" @patch-signers-order="validateSignersOrder"
      @reset-signers-order="$emit('resetSigners')" />

    <WitnessDialog v-if="isWitnessDialogOpen"
                   :signers="file.objectToPost.signers"
                   :file-id="file.id"
                   :witnesses="file.objectToPost.witnesses"
                   @close="isWitnessDialogOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, PropType } from 'vue'
import { TranslateResult } from 'vue-i18n'
import { createNamespacedHelpers, mapGetters } from 'vuex'

import Alert from '@/common/alerts/Alert.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { canAddRecipientOnEnvelope, ENVELOPE_RECIPIENTS_MAX_COUNT } from '@/common/enveloppe/envelope-recipients'
import SearchTextField from '@/common/SearchTextField.vue'
import EnvelopeUsersSelector from '@/common/users/envelopeUsersSelector/EnvelopeUsersSelector.vue'
import { formatNumberedSignerName } from '@/common/utils/signingChecklist'
import AppAlertInPage from '@/design-system/alert/AppAlertInPage.vue'
import {
  ENVELOPE_TYPES,
  SIGNATURE_TYPES,
  SIGNATURE_TYPE_DOCUSIGN_ADVANCED,
  SIGNATURE_TYPE_DOCUSIGN_QUALIFIED,
  SIGNATURE_TYPE_DOCUSIGN_SIMPLE,
  SIGNATURE_TYPE_HANDWRITTEN,
} from '@/project/signing-checklist/constants'
import SignersOrderDialog from '@/project/signing-checklist/order-signature/SignersOrderDialog.vue'
import SignersOrderQualifiedDialog from '@/project/signing-checklist/order-signature/SignersOrderQualifiedDialog.vue'
import WitnessDialog from '@/project/signing-checklist/witness/WitnessDialog.vue'
import { SigningChecklistAddProvidedData, signingChecklistAddInjectionKey } from '@/project/views/inject-keys/signingChecklistAddInjectionKey'
import { GenericSignature, SigningChecklistFile } from '@/project/views/SigningChecklistAdd.vue'
import { SET_SIGNERS_GROUP_ORDER } from '@/store/modules/signing-checklist/mutation_types'

import SigningChecklistTemplateSignersDetails from './SigningChecklistTemplateSignersDetails.vue'

const { mapMutations: mapSigningChecklistMutations } = createNamespacedHelpers('signingChecklist')

export default defineComponent({
  name: 'Section4',
  components: {
    AppAlertInPage,
    SigningChecklistTemplateSignersDetails,
    Alert,
    AppTooltip,
    EnvelopeUsersSelector,
    SearchTextField,
    SignersOrderDialog,
    SignersOrderQualifiedDialog,
    WitnessDialog,
  },
  props: {
    file: {
      type: Object as PropType<SigningChecklistFile>,
      required: true,
    },
    hasSignersOrder: {
      type: Boolean,
      required: true,
    },
    isEditingLimited: {
      type: Boolean,
      required: true,
    },
  },
  setup () {
    const signingChecklistAddProvidedData = inject<SigningChecklistAddProvidedData>(signingChecklistAddInjectionKey)

    return {
      addFromTemplate: signingChecklistAddProvidedData?.addFromTemplate,
    }
  },
  provide () {
    return {
      canAddRecipient: computed(() => this.canAddRecipient),
    }
  },
  data () {
    return {
      ENVELOPE_RECIPIENTS_MAX_COUNT,
      SIGNATURE_TYPE_HANDWRITTEN,
      isSignersOrderQualifiedDialogOpen: false,
      isWitnessDialogOpen: false,
      searchQuery: '',
      signatoriesOrderDialog: false,
    }
  },
  computed: {
    ...mapGetters('groups', ['allRoomMembers']),
    ...mapGetters('room', ['isWitnessEnabled']),
    hasAtLeastOneSigner (): boolean {
      return this.file.objectToPost.signers?.flat().length > 0
    },
    hasMultipleSigners (): boolean {
      return this.file.objectToPost.signers?.flat().length > 1
    },
    isMultipleEnvelope (): boolean {
      return this.file.objectToPost.envelopeType === ENVELOPE_TYPES.MULTIPLE
    },
    isSignersOrderButtonDisplayed (): boolean {
      return this.file.objectToPost.signatureType !== SIGNATURE_TYPE_HANDWRITTEN &&
        !this.isMultipleEnvelope
    },
    isQualifiedSignatoriesOrderAlertDisplayed (): boolean {
      return this.file.objectToPost.signatureType === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED &&
        this.hasMultipleSigners &&
        !this.hasSignersOrder
    },
    templateGenericSigners (): Array<string> {
      return (this.file.templateSignatures ?? [])
        .filter((templateSignature: GenericSignature) => templateSignature.signerType === SIGNATURE_TYPES.SINGLE)
        .map((templateSignature: GenericSignature, index) => templateSignature.signerName ?? formatNumberedSignerName(templateSignature, index + 1, this.isMultipleEnvelope))
    },
    isWitnessButtonDisplayed (): boolean {
      const witnessCompatibleSignatureTypes = [
        SIGNATURE_TYPE_DOCUSIGN_SIMPLE,
        SIGNATURE_TYPE_DOCUSIGN_ADVANCED,
        SIGNATURE_TYPE_DOCUSIGN_QUALIFIED,
      ]
      return witnessCompatibleSignatureTypes.includes(this.file.objectToPost.signatureType) && this.isWitnessEnabled
    },
    witnessButtonTooltip (): TranslateResult | null {
      const witnessesFeatureNotAvailable = this.file.objectToPost.envelopeType === ENVELOPE_TYPES.MULTIPLE
        || this.file.objectToPost.signatureType === SIGNATURE_TYPE_DOCUSIGN_ADVANCED
        || this.file.objectToPost.signatureType === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED

      if (witnessesFeatureNotAvailable) {
        return this.$t('project.signing-checklist.add.step2.Section4.addWitnessErrorSignatureMultipleTooltip')
      }

      return this.$t('project.signing-checklist.add.step2.Section4.addWitnessTooltip')
    },
    hasWitnessButtonTooltip (): boolean {
      return this.isWitnessButtonDisabled && this.canAddRecipient
    },
    isWitnessButtonDisabled (): boolean {
      if (this.file.objectToPost.envelopeType === ENVELOPE_TYPES.MULTIPLE) {
        return true
      }

      if (this.file.objectToPost.signatureType !== SIGNATURE_TYPE_DOCUSIGN_SIMPLE) {
        return true
      }

      return !this.hasAtLeastOneSigner || this.isEditingLimited
    },
    qualifiedSignatureHelpLink () {
      if (this.$i18n.locale === 'fr') {
        return 'https://help.closd.com/fr/knowledge-base/signature-qualifiee-ordonner-les-signataires/'
      }
      return 'https://help.closd.com/en/knowledge-base/qualified-electronic-signature-order-the-signatories/'
    },
    canAddRecipient () {
      return canAddRecipientOnEnvelope(this.file.objectToPost)
    },
  },
  methods: {
    ...mapSigningChecklistMutations([SET_SIGNERS_GROUP_ORDER]),
    validateSignersOrder (groups: number[]): void {
      this[SET_SIGNERS_GROUP_ORDER]({
        groups,
        file: this.file,
      })
    },
    onSignersOrderClick (): void {
      if (this.file.objectToPost.signatureType === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED) {
        this.isSignersOrderQualifiedDialogOpen = true
      } else {
        this.signatoriesOrderDialog = true
      }
    },
  },
})
</script>
